/**
 * dataChot.ts — phần thuần của thẻ «Data & chốt»: kỳ xem và cách hiện số. Ô thiếu số hiện "—",
 * KHÔNG hiện 0 (thiết kế mục 11).
 */
import { formatIntVN } from "@/lib/format";
import { toLocalDate } from "@/lib/week-days";

export type ChatPeriodKind = "today" | "yesterday" | "days7" | "month" | "custom";
export type ChatRange = { from: string; to: string };

export const CHAT_PERIODS: { kind: ChatPeriodKind; label: string }[] = [
  { kind: "today", label: "Hôm nay" },
  { kind: "yesterday", label: "Hôm qua" },
  { kind: "days7", label: "7 ngày" },
  { kind: "month", label: "Tháng này" },
  { kind: "custom", label: "Tự chọn" },
];

export const CHAT_BRANDS = [
  { code: "", label: "Tất cả thương hiệu" },
  { code: "GNCA", label: "Gạo Nâu Chụp Ảnh" },
  { code: "GNPF", label: "Gạo Nâu Profile" },
  { code: "GNAT", label: "Gạo Nâu Chụp Ảnh Thẻ" },
  { code: "GNTR", label: "Gạo Nâu Travel" },
  { code: "GNPM", label: "Gạo Nâu Makeup" },
] as const;

export function todayVN(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Ho_Chi_Minh",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

function shiftDay(day: string, n: number): string {
  const [y = 0, m = 1, d = 1] = day.split("-").map(Number);
  return toLocalDate(new Date(y, m - 1, d + n));
}

export function rangeOf(kind: Exclude<ChatPeriodKind, "custom">, today: string): ChatRange {
  switch (kind) {
    case "today":
      return { from: today, to: today };
    case "yesterday": {
      const y = shiftDay(today, -1);
      return { from: y, to: y };
    }
    case "days7":
      return { from: shiftDay(today, -6), to: today };
    case "month":
      return { from: `${today.slice(0, 7)}-01`, to: today };
  }
}

/** "2026-10-30" → "30/10/2026". */
export function dayText(day: string): string {
  const [y, m, d] = day.split("-");
  return y && m && d ? `${d}/${m}/${y}` : day;
}

export function maybeInt(v: number | null | undefined): string {
  return v == null ? "—" : formatIntVN(v);
}

/** 8.79 → "8,79%". */
export function pctText(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${v.toLocaleString("vi-VN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

/** Giờ máy tính số theo giờ Việt Nam: "23:10 30/10". */
export function updatedAtText(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const f = new Intl.DateTimeFormat("vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => f.find((p) => p.type === t)?.value ?? "";
  return `${get("hour")}:${get("minute")} ${get("day")}/${get("month")}`;
}

const SOURCE_LABEL: Record<string, string> = {
  sale_daily: "số theo trang",
  sale_cohort: "lứa khách",
  sale_daily_staff: "thẻ theo người gắn",
  staff_pancake_stats: "thống kê tài khoản",
  sale_daily_assignee: "số theo người được giao",
  sale_new_conv: "lần trả lời đầu",
  chat_channels: "danh sách trang",
  cuoc_chat_trang_thai: "lần trả lời đầu",
};

export function sourceLabel(code: string): string {
  return SOURCE_LABEL[code] ?? "một nguồn số";
}

/** Tỉ lệ rớt so với nấc trước. Nấc trước bằng 0 thì không có tỉ lệ. */
export function funnelPct(cur: number, prev: number): number | null {
  return prev > 0 ? Math.round((cur * 10000) / prev) / 100 : null;
}

/** 180 giây → "3 phút"; dưới một phút ghi giây. */
export function secondsText(v: number | null | undefined): string {
  if (v == null) return "—";
  return v < 60 ? `${v} giây` : `${Math.round(v / 60)} phút`;
}

/** 90 phút → "1,5 giờ". */
export function hoursText(minutes: number): string {
  return `${(Math.round(minutes / 6) / 10).toLocaleString("vi-VN")} giờ`;
}
